import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity with custom increments.
 * Extends the base QuantitySelectorComponent functionality.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorIncrementComponent extends Component {
  /**
   * Gets the increment value from the data attribute or defaults to 1.
   * @returns {number} The increment value.
   */
  get increment() {
    return parseInt(this.dataset.increment) || 1;
  }

  /**
   * Gets the available inventory quantity from the data attribute.
   * @returns {number} The available inventory quantity.
   */
  get inventoryQuantity() {
    return parseInt(this.dataset.inventoryQuantity) || 0;
  }

  /**
   * Gets the minimum value from the input or defaults to 0.
   * @returns {number} The minimum value.
   */
  get minValue() {
    return parseInt(this.refs.quantityInput.min) || 0;
  }

  /**
   * Gets the maximum allowed quantity considering stock and variant rules.
   * @returns {number} The maximum allowed quantity.
   */
  get maxAllowedQuantity() {
    const variantMax = this.refs.quantityInput.max ? parseInt(this.refs.quantityInput.max) : Infinity;
    const stockMax = this.inventoryQuantity;
    return Math.min(variantMax, stockMax);
  }

  /**
   * Snaps a value to the nearest valid increment within stock limits.
   * @param {number} value - The value to snap.
   * @returns {number} The snapped value.
   */
  snapToIncrement(value) {
    const min = this.minValue;
    const increment = this.increment;
    const maxAllowed = this.maxAllowedQuantity;
    
    if (value <= min) {
      return min;
    }
    
    if (value >= maxAllowed) {
      // Find the largest valid increment that doesn't exceed stock
      const stepsFromMin = Math.floor((maxAllowed - min) / increment);
      return min + (stepsFromMin * increment);
    }
    
    // Calculate how many increments above minimum
    const stepsFromMin = Math.round((value - min) / increment);
    return min + (stepsFromMin * increment);
  }

  /**
   * Handles the quantity increase event with custom increment.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const currentValue = parseInt(this.refs.quantityInput.value) || this.minValue;
    const increment = this.increment;
    const newValue = Math.min(currentValue + increment, this.maxAllowedQuantity);
    
    this.refs.quantityInput.value = newValue.toString();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event with custom increment.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const currentValue = parseInt(this.refs.quantityInput.value) || this.minValue;
    const increment = this.increment;
    const newValue = Math.max(this.minValue, currentValue - increment);
    
    this.refs.quantityInput.value = newValue.toString();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event with increment validation.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      const inputValue = parseInt(event.target.value) || this.minValue;
      const snappedValue = this.snapToIncrement(inputValue);
      this.refs.quantityInput.value = snappedValue.toString();
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met and snaps to increment
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, value: currentValue } = quantityInput;
    let newValue = parseInt(currentValue) || this.minValue;

    // Snap to increment first
    newValue = this.snapToIncrement(newValue);

    // Then apply min constraint
    if (min && newValue < parseInt(min)) {
      newValue = parseInt(min);
    }

    // Apply max constraint considering both variant rules and stock
    const maxAllowed = this.maxAllowedQuantity;
    if (newValue > maxAllowed) {
      newValue = this.snapToIncrement(maxAllowed);
    }

    quantityInput.value = newValue.toString();
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-increment-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-increment-component')) {
  customElements.define('quantity-selector-increment-component', QuantitySelectorIncrementComponent);
}
